/**
 * Orden por rank: comparar y reordenar. **Generar, no.**
 *
 * El `priority_rank` lo calcula el servidor (LexoRank base-36 en minúsculas,
 * plan 2.3). El cliente solo dice ENTRE QUÉ VECINOS quiere el ítem y ordena lo
 * que le devuelven. El contrato completo y por qué es así está arriba de
 * `shared/hooks/use-reorder-mutation.ts`.
 */
export type Rank = string

/**
 * Compara dos ranks por BYTES, que es exactamente lo que hace la columna del
 * backend: `varchar(64) COLLATE "C"`.
 *
 * **No usar `localeCompare`.** El locale ignora mayúsculas, acentos y ceros a la
 * izquierda; la colación "C" no. Acá replicamos la del servidor para que el
 * orden que se ve sea el mismo que el que se persiste.
 */
export function compareRanks(a: Rank, b: Rank): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/**
 * Los vecinos de un movimiento: lo ÚNICO que viaja al server.
 *
 * `ordered` es la lista ya ordenada, `activeIndex` de dónde sale el ítem y
 * `overIndex` dónde quedó. El ítem se saca primero de la lista para que no
 * cuente como su propio vecino: sin eso, mover una fila un lugar hacia abajo
 * manda como vecino al ítem que se está moviendo.
 *
 * - `beforeId: null` = primer puesto de la lista.
 * - `afterId: null`  = último puesto.
 */
export function neighborsForMove<T extends { id: string }>(
  ordered: readonly T[],
  activeIndex: number,
  overIndex: number,
): { beforeId: string | null; afterId: string | null } {
  const without = ordered.filter((_, index) => index !== activeIndex)

  return {
    beforeId: without[overIndex - 1]?.id ?? null,
    afterId: without[overIndex]?.id ?? null,
  }
}

/**
 * El update optimista: mueve `id` entre sus vecinos **reordenando el arreglo**,
 * sin inventar ningún rank. El rank sigue siendo el viejo hasta que conteste el
 * server; como nada vuelve a ordenar por rank en pantalla (las listas se
 * ordenan al llegar del API, no en cada render), la fila se ve en su lugar
 * nuevo en el mismo frame.
 *
 * Devuelve el mismo arreglo si no hay nada que mover: si el ítem no está, si
 * los vecinos no pertenecen a esta lista (una lista filtrada, `/due-soon`) o si
 * no vino ninguno. Reordenar a ciegas ahí sería peor que no tocar nada.
 */
export function moveByNeighbors<T extends { id: string }>(
  items: readonly T[],
  id: string,
  beforeId: string | null,
  afterId: string | null,
): T[] {
  const from = items.findIndex((item) => item.id === id)
  if (from === -1) return [...items]
  if (beforeId === null && afterId === null) return [...items]

  const rest = items.filter((_, index) => index !== from)
  const indexOf = (target: string | null) =>
    target === null ? -1 : rest.findIndex((item) => item.id === target)

  // Con los dos vecinos alcanza con uno: el índice de `after` en la lista sin el
  // ítem es exactamente el de `before` más uno.
  const before = indexOf(beforeId)
  const after = indexOf(afterId)

  const target =
    before !== -1
      ? before + 1
      : beforeId === null
        ? 0 // primer puesto: sigue siendo el primero en cualquier sublista
        : after !== -1
          ? after
          : afterId === null
            ? rest.length // último puesto
            : -1

  if (target === -1) return [...items] // ningún vecino vive en esta lista

  rest.splice(target, 0, items[from]!)
  return rest
}

/** Dónde queda un ítem respecto de su ancla: justo encima o justo debajo. */
export interface Anchor {
  id: string
  placement: 'above' | 'below'
}

/**
 * El ancla de un drop en una lista que puede estar **filtrada**.
 *
 * Entre dos filas visibles puede haber ítems ocultos, así que no alcanza con
 * mandar los dos vecinos visibles: el servidor no sabría dónde, entre los
 * ocultos, va el movido. Se manda UN vecino y un lado, y el servidor busca el
 * vecino real (`POST /projects/{id}/move` con `anchor_id` + `placement`):
 *
 * - si **subió**, se pega encima de la fila que le quedó debajo;
 * - si **bajó**, se pega debajo de la fila que le quedó arriba.
 *
 * Así nunca salta por encima de nada oculto: soltarlo arriba de todo en una
 * vista filtrada lo deja justo antes del primero visible, no en el #1 global.
 *
 * `eligible` descarta anclas de otra lista de prioridad (los permanentes): si la
 * fila de al lado no sirve, se busca la próxima que sí en la misma dirección.
 * `null` = no hay ninguna fila válida a la cual pegarse.
 */
export function anchorForDrop<T extends { id: string }>(
  items: readonly T[],
  fromIndex: number,
  toIndex: number,
  eligible: (item: T) => boolean = () => true,
): Anchor | null {
  if (fromIndex === toIndex || !items[fromIndex]) return null
  const rest = items.filter((_, index) => index !== fromIndex)
  const subio = toIndex < fromIndex

  const buscar = (desde: number, paso: number): T | undefined => {
    for (let i = desde; i >= 0 && i < rest.length; i += paso) {
      if (eligible(rest[i]!)) return rest[i]
    }
    return undefined
  }

  // En `rest`, el movido iría en `toIndex`: debajo queda rest[toIndex] y
  // arriba rest[toIndex - 1].
  const preferida = subio ? buscar(toIndex, 1) : buscar(toIndex - 1, -1)
  if (preferida) return { id: preferida.id, placement: subio ? 'above' : 'below' }
  const otra = subio ? buscar(toIndex - 1, -1) : buscar(toIndex, 1)
  return otra ? { id: otra.id, placement: subio ? 'below' : 'above' } : null
}

/** El update optimista de un movimiento pegado: lo saca y lo inserta junto al ancla. */
export function moveNextTo<T extends { id: string }>(
  items: readonly T[],
  id: string,
  anchor: Anchor,
): T[] {
  const from = items.findIndex((item) => item.id === id)
  if (from === -1 || anchor.id === id) return [...items]
  const rest = items.filter((_, index) => index !== from)
  const at = rest.findIndex((item) => item.id === anchor.id)
  // El ancla no está en esta lista (otra vista filtrada): no se reordena a ciegas.
  if (at === -1) return [...items]
  rest.splice(anchor.placement === 'above' ? at : at + 1, 0, items[from]!)
  return rest
}
